import React, { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Card from "react-bootstrap/Card";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";
import { fetchConfigurations, updateConfigurationValue } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { useLoading } from "../../contexts/LoadingContext";

function PortalSettings() {
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  const [editWindow, setEditWindow] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchConfigurations();
      if (res.success && Array.isArray(res.data)) {
        const setting = res.data.find(cfg => cfg.keyName === "applicant_edit_window_days");
        if (setting) {
          setEditWindow(setting.value);
        } else {
          setEditWindow("7"); // default fallback
        }
      }
    } catch (err) {
      console.error("Failed to load portal settings:", err);
      setError("Failed to load portal settings from the server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editWindow || isNaN(editWindow) || parseInt(editWindow, 10) < 0) {
      showToast("Please enter a valid number of days (0 or greater).", "error");
      return;
    }

    try {
      setSaving(true);
      startLoading();
      const res = await updateConfigurationValue("applicant_edit_window_days", editWindow);
      if (res.success) {
        showToast("Portal settings updated successfully!", "success");
      }
    } catch (err) {
      console.error("Failed to update portal settings:", err);
      showToast(err.response?.data?.message || "Failed to save settings.", "error");
    } finally {
      setSaving(false);
      stopLoading();
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5 py-5">
        <Spinner animation="border" variant="success" />
        <p className="mt-2 text-muted">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="subMain-content">
      <div className="page-header mb-4">
        <h1 className="page-title">Portal Settings</h1>
        <p className="page-subtitle text-muted">
          Configure applicant portal behavior and editing constraints.
        </p>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm border-0" style={{ borderRadius: "12px", maxWidth: "600px" }}>
        <Card.Body className="p-4">
          <Form onSubmit={handleSave}>
            <Form.Group className="mb-4" controlId="editWindowDays">
              <Form.Label className="fw-bold text-dark">
                Applicant Edit Window (Days)
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={editWindow}
                onChange={(e) => setEditWindow(e.target.value)}
                placeholder="7"
                style={{
                  borderRadius: "8px",
                  padding: "0.6rem 0.8rem",
                  border: "1px solid #cbd5e1"
                }}
              />
              <Form.Text className="text-muted mt-2 d-block">
                The number of days after submission that an applicant is allowed to edit their application (as long as the application is still "Pending"). Set to 0 to disable editing completely.
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="outline-secondary"
                onClick={loadSettings}
                disabled={saving}
                style={{ borderRadius: "8px", padding: "0.5rem 1.5rem" }}
              >
                Reset
              </Button>
              <Button
                type="submit"
                disabled={saving}
                style={{
                  backgroundColor: "#275317",
                  borderColor: "#275317",
                  borderRadius: "8px",
                  padding: "0.5rem 2rem",
                  color: "#fff",
                  fontWeight: "600"
                }}
              >
                {saving ? "Saving..." : "Save Settings"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
}

export default PortalSettings;
